// Status → colour mapping (spec "Status colour mapping"). CRITICAL accessibility rule: status is
// NEVER colour-only. Every dot/chip that carries a status colour ALSO carries a text label — the
// colour is redundant reinforcement, never the sole signal. These helpers return both the CSS
// colour var AND the human label so callers cannot accidentally render colour alone.

import type { WorkItem, WorkPriority } from "./types";

export interface StatusVisual {
  /** CSS colour for the dot/accent. */
  color: string;
  /** Human label — ALWAYS rendered alongside the colour. */
  label: string;
}

// The mapping keys on semantic role, resolved from the tenant status slug + terminal flag. We do
// NOT hardcode tenant status names (those are per-tenant rows); we map by role heuristics with a
// neutral default, so a tenant renaming "done" still gets the right treatment.
export function statusVisual(slug: string, label: string, isTerminal: boolean): StatusVisual {
  const s = slug.toLowerCase();
  if (isTerminal || s.includes("done") || s.includes("resolved") || s.includes("closed"))
    return { color: "var(--teal)", label }; // Done — the only place nectar green appears
  if (s.includes("progress") || s.includes("doing") || s === "in_progress")
    return { color: "var(--gold)", label }; // In progress — the one thing the eye finds first
  if (s.includes("wait") || s.includes("blocked") || s.includes("hold"))
    return { color: "var(--brown-light)", label }; // Waiting — warm but inert
  if (s.includes("triage") || s.includes("todo") || s.includes("new") || s.includes("backlog"))
    return { color: "var(--text-4)", label }; // Deliberately colourless — nothing decided yet
  return { color: "var(--text-4)", label };
}

export interface PriorityVisual {
  pillClass: string;
  label: string;
}

export function priorityVisual(p: WorkPriority): PriorityVisual {
  switch (p) {
    case "urgent":
      return { pillClass: "pill-red", label: "Urgent" };
    case "high":
      return { pillClass: "pill-gold", label: "High" };
    case "normal":
      return { pillClass: "pill-neutral", label: "Normal" };
    case "low":
      return { pillClass: "pill-brown", label: "Low" };
  }
}

// Channel chips (spec): WhatsApp teal-dim, Email blue-dim, Hive gold-dim.
export function channelChip(source: WorkItem["source"]): { cls: string; label: string } | null {
  switch (source) {
    case "whatsapp":
      return { cls: "chip-wa", label: "WhatsApp" };
    case "email":
      return { cls: "chip-em", label: "Email" };
    case "hive":
      return { cls: "chip-hv", label: "Hive" };
    default:
      return null;
  }
}
